<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { z } from "zod";
import { TrashIcon, AtSymbolIcon} from '@heroicons/vue/24/outline';
import { FolderPlusIcon } from '@heroicons/vue/24/solid';
import AlertModal from '@/components/ui/modals/AlertModal.vue';
import Select from '@/components/ui/select/Select.vue';
import adminAPI from '@/services/api';
import { useNotificationStore } from '@/store';
import { adminRoles } from '@/utils/config/constants';
import type { ListBusinessTravelCompanies, UpdateTravelAgencyCompanyPayload } from "@upclaim/sdk/src/data-contracts"
import SetTravelAgentForCompany from '@/components/companies/SetTravelAgentForCompany.vue';
import BaseButton from "@/components/ui/buttons/BaseButton.vue";
import EditableInput from '@/components/ui/inputs/EditableInput.vue';
import { Building2, UserCheckIcon } from 'lucide-vue-next';
import CompanyModal from '@/components/modals/CompanyModal.vue';
import ToggleWithIcon from '@/components/ui/toggles/ToggleWithIcon.vue';

const apiResponse = ref();
onMounted(async () => apiResponse.value = await adminAPI.listBusinessTravelCompanies());
watch(apiResponse, (data) => {
  if (data) {
    companies.value = data;
  }
});

const companies = ref<ListBusinessTravelCompanies>([])
const admins = ref<any>([]);

onMounted(async () => {
  admins.value = await adminAPI.listAdmin()
})

const selectedCompanies = ref<string[]>([])
const indeterminate = computed(() => selectedCompanies.value.length > 0 && selectedCompanies.value.length < companies.value.length)

/// Create company form
const modalOpen = ref(false);
const defaultForm = {
  domain: "",
  displayName: "",
  bluebizIdentifier: null as any,
  mainContactEmail: null as any,
  siren: null as any,
}
const createCompanyForm = ref({...defaultForm});
async function createCompany() {
  const { domain, displayName, bluebizIdentifier, mainContactEmail } = createCompanyForm.value;
  const { success } = await adminAPI.createTravelAgencyCompany([{
    domain,
    displayName,
    bluebizIdentifier,
    mainContactEmail,
  }]);
  modalOpen.value = false;
  if (success) {
    createCompanyForm.value = defaultForm;
    useNotificationStore().emitNotification({
      type: 'success',
      title: `Entreprise "${displayName}" ajoutée avec succès`
    });
  }
}

async function updateCompany(companyId, data: UpdateTravelAgencyCompanyPayload) {
  const { success } = await adminAPI.updateTravelAgencyCompany(companyId, data);
  if (success) useNotificationStore().emitNotification({ title: 'Entreprise modifiée avec succès' })
}
</script>
<template>
<div class="w-full pb-16 overflow-y-auto text-left">
  <AlertModal
    :open="modalOpen"
    type="info"
    title="Créer une entreprise"
    confirm-message="Créer l'entreprise"
    @close="() => {
      createCompanyForm = defaultForm;
      modalOpen = false;
    }"
    @confirm="createCompany"
  >
    <div>
      <form class="pb-8">
        <div class="pb-3">
          <label for="display_name" class="block text-sm font-medium leading-6 text-gray-900">Nom de la société</label>
          <div class="mt-2">
            <input type="text" v-model="createCompanyForm.displayName" name="display_name" id="display_name" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              placeholder="Nom de la société"
            />
          </div>
        </div>
        <div class="pb-3">
          <label for="domain" class="block text-sm font-medium leading-6 text-gray-900">Nom de domaine</label>
          <div class="mt-2">
            <input type="text" v-model="createCompanyForm.domain" name="domain" id="domain" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              placeholder="Ex: upclaim.eu" />
          </div>
        </div>
        <div class="pb-3">
          <label for="contact" class="block text-sm font-medium leading-6 text-gray-900">SIREN</label>
          <div class="mt-2">
            <input type="text" v-model="createCompanyForm.siren" name="contact" id="contact" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
               placeholder="Numéro de SIREN" />
          </div>
        </div>
        <div class="pb-3">
          <label for="identifier" class="block text-sm font-medium leading-6 text-gray-900">Identifiant OSI YYOIN</label>
          <div class="mt-2">
            <input type="text" v-model="createCompanyForm.bluebizIdentifier" name="identifier" id="identifier" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              placeholder="Ex: FRUPCLAIM" />
          </div>
        </div>
        <div class="pb-3">
          <label for="contact" class="block text-sm font-medium leading-6 text-gray-900">Email dédié entreprise</label>
          <div class="mt-2">
            <input type="text" v-model="createCompanyForm.mainContactEmail" name="contact" id="contact" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
               placeholder="Email interne où router les notifications" />
          </div>
        </div>
      </form>
    </div>
  </AlertModal>

  <div class="px-4 pt-12 sm:px-6 lg:px-12">
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-lg font-semibold leading-6 text-gray-900 ">Entreprises</h1>
        <p class="mt-2 text-sm text-gray-700">Une liste de toutes les entreprises avec leur domaine, responsable agent et chargé de compte.</p>
      </div>
      <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
        <BaseButton text="Ajouter une entreprise" size="md" @click="modalOpen = true" :icon="(Building2 as any)" />
      </div>
    </div>
    <div class="flow-root mt-8">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-12">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div v-if="selectedCompanies.length > 0" class="absolute top-0 z-50 flex items-center h-12 space-x-3 bg-white left-14 sm:left-12">
            <BaseButton size="md" :icon="TrashIcon" text="Désactiver" :disabled="false" @click="() => useNotificationStore().emitNotification({ title: 'Merci de nous contacter directement', type: 'info' })" />
          </div>
          <div class="border rounded-lg shadow-sm">
            <table class="min-w-full">
            <thead class="">
              <tr>
                <th scope="col" class="relative px-7 sm:w-12 sm:px-6">
                    <input type="checkbox" class="absolute w-4 h-4 -mt-2 text-blue-600 border-gray-300 rounded left-4 top-1/2 focus:ring-blue-600"
                    :checked="indeterminate || (selectedCompanies.length === companies.length && companies.length > 0)" :indeterminate="indeterminate"
                    @change="(event: any) => selectedCompanies = event.target?.checked ? companies.map((p) => p.id) : []" />
                </th>
                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">Nom</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Domaine</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Email dédié</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Chargé de compte</th>
                <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-3" v-tooltip="'Contact des voyageurs en direct'">
                  <span class="ml-auto text-center"><UserCheckIcon /></span>
                  <span class="sr-only">Modifier</span>
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" v-tooltip="'Email en copie des signatures'">
                  Signatures CC
                </th>
              </tr>
            </thead>
            <tbody class="bg-white">
              <template v-if="companies.length" v-for="(company, companyIdx) in companies" :key="company.id">
                <!-- <tr class="border-t border-gray-200">
                  <th colspan="7" scope="colgroup" class="py-2 pl-4 pr-3 text-sm font-semibold text-left text-gray-900 bg-gray-50 sm:pl-3">
                    {{ company.displayName }}
                  </th>
                </tr> -->
                <tr class="hover:bg-zinc-50" :class="[companyIdx === 0 ? 'border-gray-300' : 'border-gray-200', 'border-t']">
                  <td class="relative px-7 sm:w-12 sm:px-6">
                    <div v-if="selectedCompanies.includes(company.id)" class="absolute inset-y-0 left-0 w-0.5 bg-blue-600"></div>
                    <input type="checkbox" class="absolute w-4 h-4 -mt-2 text-blue-600 border-gray-300 rounded left-4 top-1/2 focus:ring-blue-600" :value="company.id" v-model="selectedCompanies" />
                  </td>
                  <td class="max-w-xs py-4 pl-4 pr-3 text-sm font-medium text-gray-900 text-ellipsis sm:pl-3">
                    <!-- <span v-if="company.iconUrl" class="relative inline-block">
                      <img class="rounded-full h-14 w-14" :src="company.iconUrl" alt="" />
                      <span class="absolute right-0 top-0 block h-3.5 w-3.5 rounded-full bg-red-400 ring-2 ring-white" />
                    </span> -->

                    <CompanyModal :company="company.company" :relation="{ travelAgencyCompanyId: company.id }">
                      <span class="mx-1 font-semibold text-gray-700 underline cursor-pointer decoration-indigo-500/30 hover:decoration-indigo-500/50 hover:underline-offset-3 decoration-2 underline-offset-2">
                        {{ company.displayName }}
                      </span>
                      <!-- 
                      <div :class="company.companyId ? 'bg-green-500/20' : 'bg-gray-500/20'" class="flex-none p-1 absolute -top-1.5 -right-2 block rounded-full">
                        <div v-if="company.companyId" :class="company.companyId ? 'bg-green-500' : 'bg-gray-500'" class="h-1.5 w-1.5 rounded-full" />
                      </div> -->

                      <span v-if="company.companyId" class="absolute top-0 block w-1 h-1 rounded-full -right-1 ring-2 ring-white" :class="company.companyId ? 'bg-green-600 ring-2 ring-green-200' : 'bg-gray-400 ring-2 ring-gray-200'" />
                      <!-- <span v-else class="absolute top-0 block w-1 h-1 bg-yellow-600 rounded-full -right-1 ring-2 ring-yellow-300" /> -->
                    </CompanyModal>
                  </td>
                  <td class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">{{ company.domain }}</td>
                  <td class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                    <span v-if="company.mainContactEmail">{{ company.mainContactEmail }}</span>
                    <EditableInput v-else
                      placeholder="Email dédié entreprise"
                      :default="company.mainContactEmail"
                      :icon="AtSymbolIcon"
                      @submit="(mainContactEmail) => {
                        if (!mainContactEmail) useNotificationStore().emitNotification({ type: 'error', title: 'Email invalide' });
                        else {
                          updateCompany(company.id, { mainContactEmail })
                        }
                      }"
                      :validate-fn="(data) => z.string().email().safeParse(data).success"
                    />
                    <!-- <SetTravelAgentForCompany
                    :all-agents="(admins?.filter(admin => admin.adminRole?.name === 'CUSTOMER_AGENT') as any)"
                      :business-travel-company-id="company.id"
                      :selected-agent="company.defaultTravelAgent"
                      :request="{
                        key: 'defaultTravelAgentAdminId',
                        successMessage: 'Agent principal enregistré'
                      }"
                    /> -->
                  </td>
                  <td class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                    <SetTravelAgentForCompany
                      :all-agents="(admins?.filter(admin => admin.adminRole?.name === 'CUSTOMER_ACCOUNT_MANAGER') as any)"
                      :travel-agency-company-id="company.id"
                      :selected-agent="company.accountManager"
                      :request="{
                        key: 'accountManagerId',
                        successMessage: 'Chargé de comptes enregistré'
                      }"
                    />
                  </td>
                  <td class="relative py-4 pl-3 pr-4 text-sm font-medium text-right whitespace-nowrap sm:pr-3">
                    <span class="relative inline-flex">
                      <ToggleWithIcon @update:model-value="(isDirectPassengerContactAllowed) => {
                          updateCompany(company.id, { isDirectPassengerContactAllowed })
                      }
                      
                      " :default-value="company.isDirectPassengerContactAllowed" />
                    </span>
                  </td>
                  <td class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap min-w-40">
                    <span v-if="company.passengersSignaturesForwardEmail">{{ company.passengersSignaturesForwardEmail }}</span>
                    <EditableInput v-else-if="company.isDirectPassengerContactAllowed"
                      placeholder="Email en CC"
                      :default="company.passengersSignaturesForwardEmail"
                      :icon="AtSymbolIcon"
                      @submit="(passengersSignaturesForwardEmail) => {
                        if (!passengersSignaturesForwardEmail) useNotificationStore().emitNotification({ type: 'error', title: 'Email invalide' });
                        else {
                          updateCompany(company.id, { passengersSignaturesForwardEmail })
                        }
                      }"
                      :validate-fn="(data) => z.string().email().safeParse(data).success"
                    />
                    <!-- <span v-if="person.status === 'active'" class="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 rounded-md bg-green-50 ring-1 ring-inset ring-green-600/20">Actif</span> -->
                    <!-- <span v-else-if="person.status === 'invited'" class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-600/20">Invité</span> -->
                    <!-- <span v-else-if="person.status == null" class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 rounded-md bg-gray-50 ring-1 ring-inset ring-gray-600/20">En attente</span> -->
                  </td>
                </tr>
              </template>
              <!-- Loader -->
              <template v-else>
                <tr class="animate-pulse" v-for="i in 13">
                  <!-- This represents the checkbox column -->
                  <td class="px-3 py-4">
                    <div class="w-6 h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <!-- This represents the company display name -->
                  <td class="px-3 py-4">
                    <div class="w-full h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <!-- This represents other columns. Duplicate as needed. -->
                  <td class="px-3 py-4">
                    <div class="w-full h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <td class="px-3 py-4">
                    <div class="w-full h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <td class="px-3 py-4">
                    <div class="w-full h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <td v-if="i%4" class="px-3 py-4">
                    <div class="w-full h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <td v-if="i%3" class="px-3 py-4 text-right">
                    <div class="w-full h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>
